import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@patient-watch/ui/button";
import { Download } from "lucide-react";
import { toast } from "@patient-watch/ui/sonner";
import { AddTouchDialog } from "@/features/lead-gen/components/AddTouchDialog";
import { LeadGenFilterSelect } from "@/features/lead-gen/components/LeadGenFilterSelect";
import { LeadGenFiltersDialog } from "@/features/lead-gen/components/LeadGenFiltersDialog";
import { LeadGenNavTabs, LeadGenSearch } from "@/features/lead-gen/components/LeadGenNavTabs";
import { TouchesTable } from "@/features/lead-gen/components/TouchesTable";
import {
    compactFilters,
    parseTouchListSearch,
    REPLY_OUTCOME_FILTER_OPTIONS,
    touchFilterCount,
    type TouchListFilters,
} from "@/features/lead-gen/leadGenFilters";
import { useLeadGen } from "@/features/lead-gen/LeadGenContext";
import { TOUCH_QUEUE_BUCKET_LABEL, type TouchQueueBucket } from "@/features/lead-gen/schedule";
import { TOUCH_KIND_OPTIONS } from "@/features/lead-gen/types";
import { useNav } from "@/utils/nav";

export const Route = createFileRoute("/_app/sales/leads/touches/")({
    component: LeadTouchesPage,
    validateSearch: parseTouchListSearch,
});

function LeadTouchesPage() {
    const filters = Route.useSearch();
    const navigate = useNavigate({ from: "/sales/leads/touches/" });
    const { leads, setLeads, companies, templates, artifacts, exportSql } = useLeadGen();
    const [searchQuery, setSearchQuery] = useState("");

    useNav([
        { label: "Sales", route: { to: "/sales/leads" } },
        { label: "Leads", route: { to: "/sales/leads" } },
        { label: "Touches", route: { to: "/sales/leads/touches" } },
    ]);

    const setFilters = (next: TouchListFilters) => void navigate({ search: next, replace: true });
    const patchFilters = (patch: TouchListFilters) => setFilters(compactFilters({ ...filters, ...patch }));

    const leadFilterOptions = useMemo(
        () =>
            leads
                .map((lead) => ({ value: lead.lead_id, label: lead.name.trim() || "Untitled" }))
                .toSorted((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" })),
        [leads]
    );
    const companyFilterOptions = useMemo(
        () =>
            Array.from(new Set(leads.map((lead) => lead.company).filter(Boolean)))
                .toSorted((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
                .map((value) => ({ value, label: value })),
        [leads]
    );
    const queueFilterOptions = useMemo(
        () =>
            (["overdue", "undated", "today", "upcoming", "sent"] as TouchQueueBucket[]).map((value) => ({
                value,
                label: TOUCH_QUEUE_BUCKET_LABEL[value],
            })),
        []
    );

    return (
        <>
            <LeadGenNavTabs
                toolbar={
                    <>
                        <LeadGenFiltersDialog
                            activeCount={touchFilterCount(filters)}
                            onClear={() => setFilters({})}
                            title="Filter touches"
                        >
                            <LeadGenFilterSelect
                                id="touches-filter-lead"
                                label="Lead"
                                value={filters.lead ?? ""}
                                onChange={(lead) => patchFilters({ lead })}
                                options={leadFilterOptions}
                                allLabel="All leads"
                            />
                            <LeadGenFilterSelect
                                id="touches-filter-company"
                                label="Company"
                                value={filters.company ?? ""}
                                onChange={(company) => patchFilters({ company })}
                                options={companyFilterOptions}
                                allLabel="All companies"
                            />
                            <LeadGenFilterSelect
                                id="touches-filter-kind"
                                label="Kind"
                                value={filters.kind ?? ""}
                                onChange={(kind) => patchFilters({ kind: kind as TouchListFilters["kind"] })}
                                options={TOUCH_KIND_OPTIONS}
                                allLabel="All kinds"
                            />
                            <LeadGenFilterSelect
                                id="touches-filter-queue"
                                label="When"
                                value={filters.queue ?? ""}
                                onChange={(queue) => patchFilters({ queue: queue as TouchListFilters["queue"] })}
                                options={queueFilterOptions}
                                allLabel="All dates"
                            />
                            <LeadGenFilterSelect
                                id="touches-filter-reply"
                                label="Reply"
                                value={filters.reply ?? ""}
                                onChange={(reply) => patchFilters({ reply: reply as TouchListFilters["reply"] })}
                                options={REPLY_OUTCOME_FILTER_OPTIONS}
                                allLabel="All replies"
                            />
                        </LeadGenFiltersDialog>
                        <LeadGenSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search touches..." />
                    </>
                }
                action={
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="min-h-0 shrink-0"
                            onClick={() => {
                                exportSql();
                                toast.success("SQL export downloaded");
                            }}
                        >
                            <Download className="w-3.5 h-3.5" />
                            Export SQL
                        </Button>
                        <AddTouchDialog leads={leads} onLeadsChange={setLeads} />
                    </div>
                }
            />

            <TouchesTable
                leads={leads}
                companies={companies}
                templates={templates}
                artifacts={artifacts}
                filters={filters}
                onFiltersChange={setFilters}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onLeadsChange={setLeads}
            />
        </>
    );
}
